/**
 * Company Drawer Component
 *
 * Small drawer for creating/editing a company.
 * Includes branding section (logo + primary color) in edit mode.
 *
 * @see Sprint 20 - Interface Super Admin /bill-admin
 */

import { useState, useEffect } from 'react';
import { Loader2, Trash2 } from 'lucide-react';
import { Drawer, Button, Input, Select, Checkbox } from '@/components/ui';
import { FileUpload } from '@/components/ui';
import type { AdminCompanyDetail, CreateAdminCompanyInput, UpdateAdminCompanyInput } from '@bstock/shared';
import { Currency, CURRENCY_INFO } from '@bstock/shared';
import {
  useUploadCompanyLogo,
  useDeleteCompanyLogo,
} from '../hooks/useAdminMutations';

interface CompanyDrawerProps {
  company: AdminCompanyDetail | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: CreateAdminCompanyInput | UpdateAdminCompanyInput) => Promise<void>;
  isLoading?: boolean;
}

const CURRENCY_OPTIONS = Object.values(Currency).map((code) => ({
  value: code,
  label: `${code} - ${CURRENCY_INFO[code].name}`,
}));

const COUNTRY_OPTIONS = [
  { value: 'FR', label: 'France' },
  { value: 'CH', label: 'Suisse' },
  { value: 'MA', label: 'Maroc' },
  { value: 'BE', label: 'Belgique' },
  { value: 'LU', label: 'Luxembourg' },
];

const DEFAULT_PRIMARY_COLOR = '#1E3A5F';

export function CompanyDrawer({
  company,
  open,
  onOpenChange,
  onSave,
  isLoading,
}: CompanyDrawerProps) {
  const isEditing = !!company;

  const [name, setName] = useState('');
  const [currency, setCurrency] = useState<Currency>(Currency.EUR);
  const [country, setCountry] = useState('FR');
  const [isActive, setIsActive] = useState(true);
  const [primaryColor, setPrimaryColor] = useState(DEFAULT_PRIMARY_COLOR);

  const uploadLogoMutation = useUploadCompanyLogo();
  const deleteLogoMutation = useDeleteCompanyLogo();

  // Reset form when company changes
  useEffect(() => {
    if (company) {
      setName(company.name);
      setCurrency(company.currency);
      setCountry(company.country);
      setIsActive(company.isActive);
      setPrimaryColor(company.primaryColor ?? DEFAULT_PRIMARY_COLOR);
    } else {
      setName('');
      setCurrency(Currency.EUR);
      setCountry('FR');
      setIsActive(true);
      setPrimaryColor(DEFAULT_PRIMARY_COLOR);
    }
  }, [company, open]);

  const handleSubmit = async () => {
    if (isEditing) {
      const data: UpdateAdminCompanyInput = {
        name,
        currency,
        country,
        isActive,
        primaryColor,
      };
      await onSave(data);
    } else {
      const data: CreateAdminCompanyInput = {
        name,
        currency,
        country,
      };
      await onSave(data);
    }
    onOpenChange(false);
  };

  const handleLogoUpload = (file: File) => {
    if (!company) return;
    uploadLogoMutation.mutate({ companyId: company.id, file });
  };

  const handleLogoDelete = () => {
    if (!company) return;
    if (window.confirm('Supprimer le logo de cette entreprise ?')) {
      deleteLogoMutation.mutate(company.id);
    }
  };

  const hasLogo = !!company?.logoStoragePath;

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title={isEditing ? "Modifier l'entreprise" : 'Nouvelle entreprise'}
      size="default"
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} isLoading={isLoading} disabled={!name.trim()}>
            {isEditing ? 'Enregistrer' : 'Creer'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Input
          label="Nom"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Nom de l'entreprise"
          required
        />

        <Select
          label="Devise"
          value={currency}
          onChange={(value) => setCurrency(value as Currency)}
          options={CURRENCY_OPTIONS}
        />

        <Select
          label="Pays"
          value={country}
          onChange={(value) => setCountry(value)}
          options={COUNTRY_OPTIONS}
        />

        {isEditing && (
          <div className="pt-2">
            <Checkbox
              label="Entreprise active"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
          </div>
        )}

        {/* Section Branding — uniquement en mode edition */}
        {isEditing && (
          <div className="pt-4 border-t border-neutral-200">
            <h4 className="text-sm font-semibold text-neutral-700 uppercase tracking-wide mb-4">
              Branding
            </h4>

            {/* Logo */}
            <div className="mb-4">
              {hasLogo && company?.logoUrl && (
                <div className="flex items-center gap-3 mb-3 p-3 bg-neutral-50 border border-neutral-200 rounded-lg">
                  <img
                    src={company.logoUrl}
                    alt="Logo"
                    className="w-12 h-12 object-contain rounded"
                  />
                  <p className="flex-1 text-sm font-medium text-neutral-700">Logo actuel</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleLogoDelete}
                    disabled={deleteLogoMutation.isPending}
                    className="text-danger-600 hover:text-danger-700 hover:bg-danger-50 shrink-0"
                  >
                    {deleteLogoMutation.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              )}

              <FileUpload
                label={hasLogo ? 'Remplacer le logo' : 'Logo'}
                accept=".jpg,.jpeg,.png,.svg"
                maxSizeMB={2}
                onFileSelect={handleLogoUpload}
                uploading={uploadLogoMutation.isPending}
              />
            </div>

            {/* Couleur primaire */}
            <div>
              <label className="block text-sm font-medium text-dark-500 mb-1.5">
                Couleur primaire
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="w-10 h-10 rounded-lg border border-neutral-200 cursor-pointer p-0.5 bg-white"
                />
                <span className="text-sm text-neutral-600 font-mono">{primaryColor}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </Drawer>
  );
}
